import { Amount, AmountUnit } from '../models';
import PWCore, { ChainID } from '..';
const FEE_BASE = 1000;
export class Builder {
    constructor(feeRate = Builder.MIN_FEE_RATE, collector = PWCore.defaultCollector, witnessArgs = null) {
        this.feeRate = feeRate;
        this.collector = collector;
        this.witnessArgs = witnessArgs;
        this.witnessArgsFixed = false;
        if (this.witnessArgs !== null)
            this.witnessArgsFixed = true;
    }
    static calcFee(tx, feeRate = Builder.MIN_FEE_RATE) {
        if (feeRate < Builder.MIN_FEE_RATE) {
            feeRate = Builder.MIN_FEE_RATE;
        }
        const txSize = tx.getSize();
        const fee = (feeRate / FEE_BASE) * txSize;
        return new Amount(fee.toString(), AmountUnit.shannon);
    }
    /**
     * Determine the WitnessArgs structure to use based on the specified lock script.
     *
     * TODO: Need more lock support here.
     *
     * @param lockScript The lock script to
     * @returns The WitnessArgs structure required by the lock script.
     */
    static determineWitnessArgs(lockScript) {
        if (lockScript.sameCodeTypeWith(PWCore.config.defaultLock.script))
            return Builder.WITNESS_ARGS.Secp256k1;
        else if (lockScript.sameCodeTypeWith(PWCore.config.pwLock.script))
            return PWCore.chainId === ChainID.ckb
                ? Builder.WITNESS_ARGS.Secp256k1 // The Mainnet release uses the standard witness args length.
                : Builder.WITNESS_ARGS.Secp256k1Pw;
        // The Testnet release requires a platform code making it one byte longer.
        else if (Object.prototype.hasOwnProperty.call(PWCore.config, 'omniLock') &&
            lockScript.sameCodeTypeWith(PWCore.config.omniLock.script))
            return Builder.WITNESS_ARGS.Secp256k1Omni;
        else
            throw new Error('A lock script was specified that has not been implemented.');
    }
    // Set the witness args based on the provided lock script.
    calculateWitnessArgs(lockScript) {
        // If witness args were provided in the options, then do not change them.
        if (this.witnessArgsFixed) {
            console.warn(`calculateWitnessArgs() cannot be used when witnessArgs was specified in the constructor.`);
            return;
        }
        this.witnessArgs = Builder.determineWitnessArgs(lockScript);
    }
    getFee() {
        return this.fee;
    }
}
Builder.MIN_FEE_RATE = 1000;
Builder.MIN_CHANGE = new Amount('61', AmountUnit.ckb); // TODO: This will be wrong for some change addresses and should be calculated in real time.
Builder.WITNESS_ARGS = {
    // SECP256k1 is used for PW-Lock on the Testnet only.
    Secp256k1Pw: {
        lock: '0x' + '0'.repeat(132),
        input_type: '',
        output_type: '',
    },
    // Omni Lock Witness Structure: https://github.com/XuJiandong/docs-bank/blob/master/omni_lock.md#omni-lock-witness
    Secp256k1Omni: {
        lock: '0x' + '0'.repeat(170),
        input_type: '',
        output_type: '',
    },
    // SECP256k1 is used for the default lock in all environments and PW-Lock on the Mainnet.
    Secp256k1: {
        lock: '0x' + '0'.repeat(130),
        input_type: '',
        output_type: '',
    },
    Secp256r1: {
        lock: '0x' + '0'.repeat(600),
        input_type: '',
        output_type: '',
    },
};
//# sourceMappingURL=data:application/json;base64,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